<template>
<div
    v-if="templates != null"
>
    <v-text-field
        label="Search"
        prepend-icon="mdi-magnify"
        v-model="full_text"
        autocomplete="off"
        v-if="show_icon_bar"
    >
        <v-menu
            bottom
            offset-y
            slot="append-outer"
        >
            <template v-slot:activator="{ on }">
                <v-icon
                    v-on="on"
                >
                    mdi-dots-vertical
                </v-icon>
            </template>
    
            <v-list>
                <v-list-item
                    v-on:click="onClickAdd()"
                     v-if="uncreated_templates != null && uncreated_templates.length > 0"
                >
                    <v-list-item-title>New...</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu> 
    </v-text-field>

    <v-data-table
        v-if="templates != null"
        :headers="headers"
        :items="templates"
        :items-per-page="100"
        class="elevation-1"
        :hide-default-footer="true"
        disable-sort
        :footer-props="{
            disableItemsPerPage: false,
            disablePagination: false,
        }"
        :search="full_text"
    >
    
        <template v-slot:item.send_from="{ item }">
            <div v-if="item.send_from == null">
                <span v-if="item.conditions?.length && item.conditions.every(({send_from}) => !!send_from)">Conditional</span>
                <span v-else class="error--text font-weight-bold">Not set</span>
            </div>
            <div v-else>
                {{pretty_send_from(item)}}
            </div>
        </template>

        <template v-slot:item.comp_best_send_mode="{ item }">
            <v-chip
                :color="getModeChipColor(item.comp_best_send_mode)"
                text-color="white"
                small
            >
                <v-icon
                    left
                    small
                >
                    {{ getModeIcon(item.comp_best_send_mode) }}
                </v-icon>
                {{ item.comp_best_send_mode.toUpperCase() }}
            </v-chip>
        </template>

        <template v-slot:item.can_send_status="{ item }">
            <v-menu
                v-if="hasIssues(item)"
                v-model="item.notes_menu_open"
                bottom
                offset-y
                close-on-click
                close-on-content-click
                max-width="400px"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-chip
                        v-bind="attrs"
                        v-on="on"
                        :color="getCanSendChipColor(item)"
                        :text-color="getCanSendChipTextColor(item)"
                        small
                        style="cursor: pointer;"
                    >
                        <v-icon
                            left
                            small
                        >
                            {{ getCanSendIcon(item) }}
                        </v-icon>
                        {{ getCanSendStatusText(item) }}
                    </v-chip>
                </template>

                <v-card>
                    <v-card-title class="py-2">
                        <span class="subtitle-1">Send Notes</span>
                    </v-card-title>
                    <v-card-text class="py-2">
                        <ul class="mb-0">
                            <li
                                v-for="(note, index) in getAllNotes(item)"
                                :key="index"
                                class="mb-1"
                            >
                                {{ note }}
                            </li>
                        </ul>
                    </v-card-text>
                </v-card>
            </v-menu>
        </template>

        <template v-slot:item.list_drip_delay="{ item }">
            {{$rc.pretty(item.list_drip_delay,'duration')}}
        </template>

        

        <template v-slot:item.message_patterns="{ item }">
            <ul
                v-if="item.message_patterns.length > 1"
            >
                <li
                    v-for="(message, index) in item.message_patterns"
                    :key="item.template_subject_id+'_'+index"
                >
                    {{message}}
                </li>
            </ul>
            <span
                v-else
            >   
                {{item.message_patterns[0]}}
            </span>
        </template>

        

        <template v-slot:item.action="{ item }">
            <v-menu 
                bottom 
                offset-y  
            >
            
            <template v-slot:activator="{ on }">
                    
                <v-icon 
                    v-on="on"
                >
                    mdi-dots-vertical-circle-outline
                </v-icon>
                    
            </template>
            
            <v-list>
                <v-list-item v-on:click="onClickTemplateSettings(item)">
                    <v-list-item-title>Settings...</v-list-item-title>
                </v-list-item>

                <v-list-item v-on:click="onClickMessages(item)">
                    <v-list-item-title>Messages...</v-list-item-title>
                </v-list-item>

                <v-divider/>

                <v-list-item v-on:click="onClickDelete(item)">    
                    <v-list-item-title>Delete...</v-list-item-title>
                </v-list-item>    

                <v-divider
                    v-if="item.upgrade_available_to != null"
                />

            </v-list>
            
            </v-menu> 
        </template>
    
    </v-data-table>

    <m-icon-bar
        v-on:add="onClickAdd"
        v-if="uncreated_templates != null && uncreated_templates.length > 0 && show_icon_bar"
    />

    <v-alert
        v-if="any_messages_in_manual_mode && show_manual_warning"
        type="warning"
        color="warning"
        class="mt-6"
        icon="mdi-information"
    >
        You have message(s) that are <strong>MANUAL</strong> mode. These messages will not be sent unless you manually review and approve them.
    </v-alert>

    <m-edit-select-dialog
        ref="select-new-template"
        v-on:ok="onCreateNewTemplate($event)"
    />

    <m-new-drip-message-dialog
        ref="new-drip-message-dialog"
        v-on:ok="update"
    />


</div>
</template>

<script>
export default {
    props: {

        organization_id: {
            type: String,
            default: function() { return this.$route.params.organization_id; }
        },

        show_column_type: {
            type: Boolean,
            default: function() { return true; }
        },

        show_column_patterns: {
            type: Boolean,
            default: function() { return false; }
        },

        show_column_delay: {
            type: Boolean,
            default: function() { return false; }
        },

        list_id: {
            type: String,
            default: function() { return null; }
        },

        show_list_label: {
            type: Boolean,
            default: function() { return true; }
        },

        show_column_params: {
            type: Boolean,
            default: function() { return true; }
        },

        show_column_notes: {
            type: Boolean,
            default: function() { return true; }
        },

        show_manual_warning: {
            type: Boolean,
            default: function() { return true; }
        },

        show_icon_bar: {
            type: Boolean,
            default: function() { return true; }
        },

        show_auto_only: {
            type: Boolean,
            default: function() { return false; }
        },
    },

    data() {
        return{
            organization_label: null,
            templates: null,
            uncreated_templates: null,
            full_text: null,
        }
    },

    mounted () {
        this.update();
    },

    // Navigation guards to ensure data refreshes when navigating to this component
    beforeRouteEnter(_to, _from, next) {
        next(vm => {
            // Force refresh when entering this route
            vm.update();
        });
    },

    beforeRouteUpdate(_to, _from, next) {
        // Force refresh when route params change
        this.update();
        next();
    },

    // For keep-alive scenarios, refresh data when component becomes active
    activated() {
        this.update();
    },

    // Watch for route changes to refresh data when query parameters change
    watch: {
        '$route'(to, from) {
            // If the route path is the same but query params changed (like our _refresh param),
            // refresh the data
            if (to.path === from.path && to.query._refresh !== from.query._refresh) {
                this.update();
            }
        }
    },

    methods:
    {
        update()
        {
            this.$rc.showSpinner();

            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/auto-messages/org-profile',
            
                'params' : {
                        'organization_id': this.organization_id,
                        'list_id': this.list_id,
                    },
            
                'on_success': (data) => {
                        this.organization_label = data.organization_label;
                        

                        if ( this.show_auto_only )
                        {
                            this.templates = data.templates.filter(
                                (item) => {
                                    return item.comp_best_send_mode == 'auto';
                                }
                            );
                        }
                        else
                        {
                            this.templates = data.templates;
                        }

                        this.uncreated_templates = data.uncreated_templates;

                        // Initialize birthday templates with default certification status to prevent loading delays
                        this.initializeBirthdayTemplateStatus();

                        // Load data cleaning certification status for birthday message templates
                        this.loadDataCleaningCertificationStatus();
                    },
            
                'on_error': 'Unable to get automated messages',
            });
        },

        onClickTemplateSettings(template)
        {
            this.$router.push({
                'name': 'auto-messages-template-settings',
                'params' : {
                    'organization_id' : template.organization_id,
                    'template_context' : template.template_context,
                    'template_subject_id': template.template_subject_id,
                }
            }).catch(e=>{}); // swallow any exception    
        },

        onClickDelete(template)
        {
            this.$rc.confirm(
                'Delete auto message template.',
                'Do you want to delete the template '+template.template_label+'.',
                [
                    {'text': 'Cancel', 'value': 'cancel'},
                    {'text': 'Confirm', 'value': 'confirm'},
                ],
                (response) => {
                    if (response == 'confirm') 
                        this.onDoDelete(template);
                }
            );
        },

        onDoDelete(template)
        {
            let params = {
                'organization_id' : template.organization_id,
                'template_context' : template.template_context,
                'template_subject_id': template.template_subject_id,
            };

            this.$rc.showSpinner();
            
            this.$rc.deleteAutoMessageTemplate(
                params,
            
                (data) => {
                    this.$rc.hideSpinner();

                    this.update();
                },
            
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to delete auto message template');
                },
            );
        },

        onClickAdd()
        {
            if ( this.list_id != null )
            {
                this.onClickAddDripMessage();
                return;
            }

            let choices = [];
            let ut = this.uncreated_templates;

            for ( let i = 0; i < ut.length; i++ )
            {
                // For system admins, show birthday message types temporarily
                if ( (ut[i].template_context === 'birthday-message' ||
                        ut[i].template_context === 'birthday-month-message' ) )
                {
                    if( !this.$rc.isAuthActorSystemAdmin() ) continue;
                    
                    choices.push({
                        'text' : ut[i].template_label,
                        'value' : ut[i].template_context,
                        'template' : ut[i],
                    });
                }

                choices.push({
                    'text' : ut[i].template_label,
                    'value' : ut[i].template_context,
                    'template' : ut[i],
                });
                    
            }

            this.$refs['select-new-template'].showDialog('Select a new automatic message type', null, choices);
        },

        onClickAddDripMessage()
        {
            let send_from = null;

            if ( this.templates != null )
            {
                for ( let i = 0; i < this.templates.length; i++ )
                {
                    send_from = this.templates[i].send_from;
                    if ( send_from != null ) break;
                }
            }

            this.$refs['new-drip-message-dialog'].showDialog(this.organization_id, this.list_id, send_from);
        },

        onCreateNewTemplate(value)
        {
            let new_template = value.template;

            this.$rc.showSpinner();
            
            this.$rc.putAutoMessageTemplate(
                new_template,
            
                (data) => {
                    this.$rc.hideSpinner();

                   this.update();
                },
            
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to create automated message');
                },
            );
        },

        onClickMessages(template)
        {
            this.$router.push({
                'name': 'auto-messages-search-messages',
                'params' : {
                    'organization_id' : template.organization_id,
                    'template_context' : template.template_context,
                    'template_subject_id': template.template_subject_id,
                }
            }).catch(e=>{}); // swallow any exception    
        },

        pretty_send_from(item)
        {
            if ( item.send_from == null )
                return "Not set - no messages will be sent";

            let tn = this.$rc.pretty(item.send_from,'endpoint');

            if ( item.send_from_label == null )
                return tn;

            return item.send_from_label+" - "+tn;
        },

        
        getCanSendStatusText(item) {
            // Always show "Notes" to indicate there are notes available
            return "Notes";
        },

        hasIssues(item) {
            const notes = this.getAllNotes(item);
            return notes.length > 0;
        },

        getAllNotes(item) {
            const notes = [];

            // Check if mode is disabled
            if (item.comp_best_send_mode === 'disabled') {
                notes.push("This template is disabled and will not send any messages. Change the send mode to 'Auto' or 'Manual' in the template settings.");
            }

            // Check if send from is set
            if (item.send_from == null) {
                notes.push("No messages can be sent because no 'Send From' number has been selected. Configure a send from number in the template settings.");
            }

            // For birthday messages check data cleaning certification
            if (item.template_context === 'birthday-message' || item.template_context === 'birthday-month-message') {
                if (item.data_cleaning_certified === false) {
                    notes.push("Birthday messages cannot be sent until you certify your data cleaning rules.");
                }
            }
            

            // Check if mode is manual
            if (item.comp_best_send_mode === 'manual') {
                notes.push("This message will only be capable to send once you manually review and approve each message. All messages will be queued for your review.");
            }

            return notes;
        },

        getCanSendChipColor(item) {
            // Always show info color for notes
            return "info";
        },

        getCanSendChipTextColor(item) {

            return "white";
        },

        getCanSendIcon(item) {
            // Always show information icon for notes
            return "mdi-information";
        },



        initializeBirthdayTemplateStatus() {
            // Initialize all birthday templates with default certification status to prevent loading delays
            if (!this.templates) return;

            this.templates.forEach(template => {
                if (template.template_context === 'birthday-message' ||
                    template.template_context === 'birthday-month-message') {
                    // Default to false until we load the actual status
                    template.data_cleaning_certified = false;
                }
            });
        },

        loadDataCleaningCertificationStatus() {
            // Check if we have any birthday message templates
            if (!this.templates) return;

            const has_birthday_templates = this.templates.some(template =>
                template.template_context === 'birthday-message' ||
                template.template_context === 'birthday-month-message'
            );

            if (!has_birthday_templates) return;

            // Load data cleaning certification status
            this.$rc.rest({
                'endpoint': 'GET: /api/v1/organization-data-cleaning-rules/:organization_id',
                'params': {
                    'organization_id': this.organization_id,
                },
                'on_success': (data) => {
                    //console.log('Data cleaning certification API response:', data);
                    // Update all birthday message templates with certification status
                    this.templates.forEach(template => {
                        if (template.template_context === 'birthday-message' ||
                            template.template_context === 'birthday-month-message') {
                            template.data_cleaning_certified = data.is_certified === true;
                            //console.log(`Updated template ${template.template_label} with certification status:`, template.data_cleaning_certified);
                        }
                    });
                    // Force reactivity update
                    this.$forceUpdate();
                },
                'on_error': () => {
                    //console.log('Data cleaning certification API error - setting all to false');
                    // If no rules exist yet, they're not certified
                    this.templates.forEach(template => {
                        if (template.template_context === 'birthday-message' ||
                            template.template_context === 'birthday-month-message') {
                            template.data_cleaning_certified = false;
                            //console.log(`Set template ${template.template_label} certification to false due to API error`);
                        }
                    });
                    // Force reactivity update
                    this.$forceUpdate();
                }
            });
        },

        getModeChipColor(mode) {
            switch(mode) {
                case 'auto':
                    return 'success';
                case 'manual':
                    return 'warning';
                case 'disabled':
                    return 'error';
                default:
                    return 'grey';
            }
        },

        getModeIcon(mode) {
            switch(mode) {
                case 'auto':
                    return 'mdi-play-circle';
                case 'manual':
                    return 'mdi-account-check';
                case 'disabled':
                    return 'mdi-stop-circle';
                default:
                    return 'mdi-help-circle';
            }
        }
    },

    computed: 
    {

        any_messages_in_manual_mode() {
            if ( this.templates == null ) return false;

            for ( let i = 0; i < this.templates.length; i++ )
            {
                if ( this.templates[i].comp_best_send_mode == 'manual' )
                    return true;
            }

            return false;
        },

        headers() {
            let ret = [];
            
            if ( this.show_column_type )
            {
                ret.push({ text: 'Type', value: 'template_context'});
            }

            if ( this.show_column_delay )
            {
                ret.push({ text: 'Delay', value: 'list_drip_delay'});
            }

            if ( this.show_list_label )
            {
                ret.push({ text: 'List', value: 'list_drip_list_internal_label'});
            }
               
            ret.push({ text: 'Label', value: 'template_label'});

            if ( this.show_column_params )
            {
                ret.push({ text: 'Parameters', value: 'param_description'});
            }

            if ( this.show_column_patterns )
            {
                ret.push({ text: 'Message', value: 'message_patterns'})
            }

            ret.push({ text: 'Send From', value: 'send_from', width: '150px'});
            ret.push({ text: 'Mode', value: 'comp_best_send_mode', align: 'center', width: '120px'});

            if ( this.show_column_notes )
            {
                ret.push({ text: 'Send Notes', value: 'can_send_status', align: 'center', width: '130px'});
            }

            ret.push({ text: 'Actions', value: 'action', align: 'right', width: '100px'});
            
            return ret;
        },
    },
}
</script>

<style lang="scss" scoped>

</style>